import React from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { InputField, TextField } from '@getgo/chameleon-web-react-wrapper';
import RedirectURIRow from 'components/redirect-uri-row-view';
import { Typography, Button, TextArea, AlertV2 } from '@getgo/chameleon-web-react-wrapper';
import { AddOutlinedIcon } from '@getgo/chameleon-icons/react';
import NudgeBanner from 'components/nudge-banner';
import environment from 'lib/environment';
import st from 'core/shared-translations';
import classes from './client-details-tab.scss';

const cgpUrl = environment.get().cgp_url;

const t = defineMessages({
  'client-edit.field.description': {
    id: 'client-edit.field.description',
    defaultMessage: 'Description (optional)',
    description: 'Label for the optional description field',
  },
  "create-token.edit.name": {
    id: "create-token.edit.name",
    defaultMessage: "Name",
    description: "Field label for user name input",
  },
  'client-edit.edit.redirectUri.text': {
    id: 'client-edit.edit.redirectUri.text',
    defaultMessage: 'After authorization, the user, along with the access token or authorization code, returns to these pages. Use URIs like https://www.example.com/',
    description: 'Label for the Redirect URI text',
  },
  'client-edit.edit.redirectUri.buttonText': {
    id: 'client-edit.edit.redirectUri.buttonText',
    defaultMessage: 'Add another URI',
    description: 'Label for the Redirect URI text',
  },
  'client-edit.validation.name-exceeds-max-length': {
    id: 'client-edit.validation.name-exceeds-max-length',
    defaultMessage: 'You cannot enter more than 128 characters.',
    description: 'Content of the error message when the entry in the AppName field exceeds more than 128 characters',
  },
  'client-edit.validation.name-required': {
    id: 'client-edit.validation.name-required',
    defaultMessage: 'Enter a client name.',
    description: 'Content of the error message when the entry in the AppName field exceeds more than 128 characters',
  },
  'client-edit.validation.description-exceeds-max-length': {
    id: 'client-edit.validation.description-exceeds-max-length',
    defaultMessage: 'You cannot enter more than 500 characters.',
    description: 'Content of the error message when the entry in the Description field exceeds more than 500 characters',
  },
  'client-edit.addRedirectUrl-malformed-uri': {
    id: 'client-edit.addRedirectUrl-malformed-uri',
    defaultMessage: 'Enter a valid URI less than 250 characters in length',
    description: 'Content of the error message when the entry in the Add redirect URI field exceeds more than 250 characters',
  },
  'client-edit.addRedirectUrl-matching-uri': {
    id: 'client-edit.addRedirectUrl-matching-uri',
    defaultMessage: 'The URI entered already exist in the Redirect URI list',
    description: 'Content of the error message when the entry in the Add redirect URI field matches another in the list',
  },

});

const ClientDetailsTab = ({
  client,
  onChange,
  onBlur,
  errors,
  addRedirectURI,
  onRedirectUriChange,
  onAddUriClick,
  isRedirectUriValid,
  onDeleteUriClick,
  canWrite,
  isInternalDev = false
}) => {
  const intl = useIntl();
  return (
    <React.Fragment>
      {!canWrite &&
        <Box id="autoApproved1" className={`row ${classes["internalClient"]}`}>
          <Box className="col-xs-12">
            <Box>
              <AlertV2>
                <FormattedMessage {...st['shared.internal.client.message']} />
              </AlertV2>
            </Box>
          </Box>
        </Box>}
      {isInternalDev &&
        <Box className={classes["detialsTabSpacing"]}>
          <NudgeBanner
            messageKey={st['shared.cgp.nudge.details']}
            url={cgpUrl}
            linkLabel={st['shared.cgp.link.label']}
          />
        </Box>}
      <Box className={classes["detialsTabSpacing"]}>
        <TextField
          id="name"
          name="name"
          role="textbox"
          value={client.name}
          title={<FormattedMessage {...t["create-token.edit.name"]} />}
          onChange={onChange}
          onBlur={onBlur}
          error={!!errors.name}
          disabled={!canWrite}
          fullwidth
          maxLength={30}
          helperText={
            (errors.name &&
              intl.formatMessage(st[errors.name] || t[errors.name])) ||
            intl.formatMessage(st["shared.name.help.text"])
          }
        >
          <FormattedMessage {...t["create-token.edit.name"]} />
        </TextField>
      </Box>
      <Box className={classes["detialsTabSpacing"]}>
        <TextArea
          id="description"
          name="description"
          helpText=""
          resize="vertical"
          label={intl.formatMessage(t['client-edit.field.description'])}
          onChange={onChange}
          value={client.description || ''}
          onBlur={onBlur}
          cols="40"
          rows="5"
          cssClass="clientDescription"
          error={
            errors.description
          }
          helperText={
            (errors.description &&
              intl.formatMessage(st[errors.description] || t[errors.description]))
          }
          fullwidth
          disabled={!canWrite}
        >
          <FormattedMessage {...t['client-edit.field.description']} />
        </TextArea>
      </Box>
      <Box>
        <Box className={classes["detialsTabSpacing"]}>
          <Typography variant="body-small-strong">
            <FormattedMessage {...st['shared.redirectUri.title']} />
          </Typography>
          <Typography color="type-color-secondary" variant="caption-medium">
            <FormattedMessage {...t['client-edit.edit.redirectUri.text']} />
          </Typography>
        </Box>
        <Box
          style={{ width: "100%" }}
          id="redirect-uri-rows">
          {!!client && !!client.redirectUris && !!client.redirectUris.length &&
            client.redirectUris.map((redirectUri, index) => (
              <RedirectURIRow
                key={index}
                position={index}
                redirectUri={redirectUri}
                uriCount={client.redirectUris.length}
                onDeleteUriClick={onDeleteUriClick}
                disabled={!canWrite}
              />
            ))}
        </Box>
        <Box className={classes["inputfieldSpacing"]}>
          <TextField
            id="addRedirectURI"
            data-testid="add-redirect-uri"
            name="addRedirectURI"
            label=""
            helpText=""
            value={addRedirectURI}
            onChange={onRedirectUriChange}
            onBlur={onBlur}
            role="textbox"
            disabled={!canWrite}
            error={
              errors.addRedirectURI
            }
            helperText={
              errors.addRedirectURI
              && intl.formatMessage(st[errors.addRedirectURI] || t[errors.addRedirectURI])
            }
            fullwidth
          />
        </Box>
        <Button
          data-testid="add-redirect-uri-button"
          className={isRedirectUriValid ? classes["addRedirectUriButton"] : ""}
          onClick={onAddUriClick}
          disabled={!isRedirectUriValid}
          variant="tertiary"
          style={{ paddingTop: "5px" }}
          leadingIcon={<AddOutlinedIcon color={`${isRedirectUriValid ? "#2C72DE" : ""}`} />}>
          <FormattedMessage {...t['client-edit.edit.redirectUri.buttonText']} />
        </Button>
      </Box>
    </React.Fragment>
  );
};

ClientDetailsTab.propTypes = {
  client: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  addRedirectURI: PropTypes.string.isRequired,
  onAddUriClick: PropTypes.func.isRequired,
  isRedirectUriValid: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  onRedirectUriChange: PropTypes.func.isRequired,
  onDeleteUriClick: PropTypes.func.isRequired,
  canWrite: PropTypes.bool.isRequired,
  isInternalDev: PropTypes.bool,
};

export default ClientDetailsTab;
